import { useCallback, useEffect, useRef } from 'react';
import { useJavascript } from '@alycecom/hooks';
import Cookies from 'js-cookie';

import { POLICY_COOKIE_NAME } from '../constants/cookies.constants';

import useTrackRecipient from './useTrackRecipient';
import useGlobalSettings from './useGlobalSettings';

export interface IEventOptions {
  [x: string]: unknown;
}

export type TUseAcceptPolicyHandler = (eventName: string, eventOptions: IEventOptions) => void;

export interface IEventPayload {
  eventName: string;
  eventOptions: IEventOptions;
}

/**
 * Hook to setup accept policy cookie.
 * @return {TUseAcceptPolicyHandler}
 */
export default (): TUseAcceptPolicyHandler => {
  const eventData = useRef<IEventPayload | null>(null);
  const trackEvent = useTrackRecipient();
  const { isSegmentInit, handleLoadSegmentSuccess } = useGlobalSettings();

  const { injectJavascript: injectSegmentService } = useJavascript('/segment.js', handleLoadSegmentSuccess);

  const handleAcceptPolicy = useCallback(
    (eventName: string, eventOptions = null) => {
      if (isSegmentInit) {
        trackEvent(eventName, eventOptions);
      } else {
        Cookies.set(POLICY_COOKIE_NAME, 'Yes', { expires: 730 });
        injectSegmentService();
        if (!eventData.current) {
          eventData.current = { eventName, eventOptions };
        }
      }
    },
    [injectSegmentService, eventData, isSegmentInit, trackEvent],
  );

  useEffect(() => {
    const hasCookie = Cookies.get(POLICY_COOKIE_NAME) !== undefined;
    if (hasCookie) {
      injectSegmentService();
    }
  }, [injectSegmentService]);

  useEffect(() => {
    const { current } = eventData;
    if (isSegmentInit && current) {
      const { eventName, eventOptions } = current;
      trackEvent(eventName, eventOptions);
    }
  }, [trackEvent, isSegmentInit, eventData]);

  return handleAcceptPolicy;
};
